import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Form, Input, Skeleton, message } from 'antd';
import React, { Suspense, lazy, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { Recipient } from '@/interface';

import { sendBulkMailRequest } from '@/redux/slices';
import { AppDispatch } from '@/redux/store';

const CardComponent = lazy(() => import('@/components/card.component'));

const schema = yup.object().shape({
  fromEmail: yup.string().required('Your email is required').email('Enter a valid email'),
  subject: yup.string().required('Subject is required').min(3, 'Subject must be at least 3 characters'),
  body: yup.string().required('Body is required').min(10, 'Body must be at least 10 characters'),
});

interface EmailFormData {
  fromEmail: string;
  subject: string;
  body: string;
}

interface CreateEmailProjectDetailProps {
  emails: Recipient[];
  projectId: string;
}

export const CreateEmailProjectDetail: React.FC<CreateEmailProjectDetailProps> = ({ emails = [], projectId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isValid },
    reset,
  } = useForm<EmailFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const onSubmit = async (data: EmailFormData) => {
    if (!emails.length) {
      message.error('No recipients provided.');
      return;
    }

    const payload = {
      recipients: emails,
      fromEmail: data.fromEmail,
      subject: data.subject,
      message: data.body,
      projectId,
    };

    setSending(true);
    try {
      await dispatch(sendBulkMailRequest({ data: payload, navigate }));
      message.success('Emails sent successfully!');
      reset();
    } catch (error) {
      console.error('Failed to send email:', error);
      message.error('Failed to send emails.');
    } finally {
      setSending(false);
    }
  };

  if (!emails.length) {
    message.error('Please provide valid email addresses');
    return null;
  }

  const handleReset = () => {
    reset({ fromEmail: '', subject: '', body: '' });
    message.warning('Form data has been reset.');
  };

  return (
    <div className="h-screen overflow-y-auto">
      <Suspense
        fallback={
          <div>
            <Skeleton paragraph={{ rows: 10 }} />
          </div>
        }
      >
        <CardComponent className="py-3 px-2 mb-4">
          <div className="text-lg font-medium">Create Email</div>
          <p className="text-sm text-gray-600 mt-2">Compose your email with subject and body text.</p>
        </CardComponent>

        <CardComponent className="px-4 py-6 mb-64">
          <Form layout="vertical" onFinish={handleSubmit(onSubmit)} className="space-y-6">
            <Form.Item label="Your Email" validateStatus={errors.fromEmail ? 'error' : ''} help={errors.fromEmail?.message || ''}>
              <Controller
                name="fromEmail"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="you@company.com"
                    onChange={(e) => {
                      setValue('fromEmail', e.target.value);
                      trigger('fromEmail');
                    }}
                  />
                )}
              />
            </Form.Item>

            <Form.Item label="Email Subject" validateStatus={errors.subject ? 'error' : ''} help={errors.subject?.message || ''}>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter email subject"
                    onChange={(e) => {
                      setValue('subject', e.target.value);
                      trigger('subject');
                    }}
                  />
                )}
              />
            </Form.Item>

            <Form.Item label="Email Body" validateStatus={errors.body ? 'error' : ''} help={errors.body?.message || ''}>
              <Controller
                name="body"
                control={control}
                render={({ field }) => (
                  <Input.TextArea
                    {...field}
                    rows={6}
                    placeholder="Write your email message here"
                    onChange={(e) => {
                      setValue('body', e.target.value);
                      trigger('body');
                    }}
                  />
                )}
              />
            </Form.Item>

            <div className="flex justify-end gap-4 mt-6">
              <Button danger onClick={handleReset} className="rounded-lg hover:bg-red-600 hover:text-white border-red-500">
                Reset Form
              </Button>

              <Button
                htmlType="submit"
                disabled={!isValid || sending}
                className={`rounded-lg px-6 ${isValid ? 'bg-green-500 hover:bg-green-700 text-white' : ''}`}
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </Form>
        </CardComponent>
      </Suspense>
    </div>
  );
};
